<template>
  <BaseModalEditCreate
    :mode="mode"
    :title="$t('common.defineSets')"
    :is-modified="isModified"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form v-if="modifiedData">
      <div>
        <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
        <BaseInput
          v-model="modifiedData.name"
          color="light"
          readonly
          class="rounded-md bg-cyan-light text-theme-700">
        </BaseInput>
      </div>
      <div>
        <label for="quantity" class="text-sm font-medium">{{ $t('warehouse.minQuantity') }}</label>
        <BaseInput v-model="modifiedData.minQuantity" type="number" color="light" class="max-w-28">
        </BaseInput>
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toast';

import { useVuelidate } from '@vuelidate/core';
import { required, helpers, minValue, maxValue } from '@vuelidate/validators';

import BaseInput from '../common/BaseInput.vue';

import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';

import { type AssortmentSet } from '@/types/Materials';
import { deepCopy } from '@/utils/data';

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    assortmentSet?: AssortmentSet;
  }>(),
  {}
);

const modifiedData = ref<AssortmentSet>();

onMounted(() => {
  if (props.assortmentSet) {
    modifiedData.value = deepCopy<AssortmentSet>(props.assortmentSet);
  }
});

const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    if (!newData) return;
    validationModel.value.minQuantity = newData.minQuantity || 0;

    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.assortmentSet);
  },
  { deep: true }
);

function onDismissChanges() {
  if (props.assortmentSet) modifiedData.value = deepCopy<AssortmentSet>(props.assortmentSet);
  emit('closeModal');
}

const toastStore = useToastStore();
async function onSave() {
  $v.value.$touch();

  if ($v.value.$invalid) {
    if ($v.value.$errors.length) toastStore.error({ text: `${$v.value.$errors[0].$message}` });
    return;
  }

  const setItems = props.assortmentSet?.items.map((item) => ({
    ...item,
    similarID: props.assortmentSet?.id,
    minQuantity: modifiedData.value?.minQuantity,
  }));
  if (!setItems) return;
  emit('save', setItems);
}

const validationModel = ref({
  minQuantity: modifiedData.value?.minQuantity || '',
});

const rules = computed(() => ({
  minQuantity: {
    required: helpers.withMessage('Min Quantity is required', required),
    minValue: helpers.withMessage('Min Quantity should be at least 1', minValue(1)),
    maxValue: helpers.withMessage(
      'Min Quantity cannot exceed the number of set items',
      maxValue(props.assortmentSet?.items.length ?? 1)
    ),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
